import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { 
  ArrowLeft, 
  ArrowRight,
  FileText,
  CheckCircle,
  Clock,
  Phone,
  Mail,
  ExternalLink
} from "lucide-react";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_ad8cae97-e844-4242-bf8a-3cc50b57fa19/artifacts/vj8eyvuz_logo%20%2B%20personaggio%20-%208energy%20srl%20%20%281%29.svg";
const OTTO_URL = "https://customer-assets.emergentagent.com/job_bill-inspector-4/artifacts/20tvm87f_8energy%20personaggio%20-%20senza%20sfondo%20con%20fiama.png";
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function SwitchGuide() {
  const [guide, setGuide] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeStep, setActiveStep] = useState(0);

  useEffect(() => {
    fetchGuide();
  }, []);

  const fetchGuide = async () => {
    try {
      const response = await fetch(`${API}/switch-guide`, {
        credentials: "include"
      });
      
      if (response.ok) {
        const data = await response.json();
        setGuide(data);
      }
    } catch (error) {
      console.error("Error fetching guide:", error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background" data-testid="switch-guide-page">
      {/* Navigation */}
      <nav className="sticky top-0 z-40 backdrop-blur-md bg-white/80 border-b border-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/dashboard" className="flex items-center gap-2">
              <img src={LOGO_URL} alt="8energy" className="h-8" />
            </Link>
            <Link to="/dashboard">
              <Button variant="ghost" size="sm" className="rounded-full" data-testid="back-btn">
                <ArrowLeft className="w-4 h-4 mr-2" />
                Dashboard
              </Button>
            </Link>
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-12"
        >
          <h1 className="text-3xl font-bold text-foreground mb-2">
            Guida al cambio fornitore
          </h1>
          <p className="text-muted-foreground max-w-2xl mx-auto">
            Passare a 8energy è semplice e gratuito. Segui questi passaggi e in poche settimane 
            inizierai a risparmiare sulle tue bollette.
          </p>
        </motion.div>

        {loading ? (
          <div className="flex justify-center py-12">
            <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
          </div>
        ) : (
          <>
            {/* Progress Bar */}
            <motion.div 
              className="mb-12"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.1 }}
            >
              <div className="flex items-center justify-between relative">
                {guide.map((step, index) => (
                  <div 
                    key={index} 
                    className="flex flex-col items-center relative z-10"
                    onClick={() => setActiveStep(index)}
                  >
                    <div 
                      className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold cursor-pointer transition-all ${
                        index <= activeStep 
                          ? "bg-primary text-white" 
                          : "bg-muted text-muted-foreground"
                      }`}
                    >
                      {index < activeStep ? (
                        <CheckCircle className="w-5 h-5" />
                      ) : (
                        index + 1
                      )}
                    </div>
                    <span className={`text-xs mt-2 hidden md:block ${
                      index <= activeStep ? "text-primary font-medium" : "text-muted-foreground"
                    }`}>
                      {step.titolo}
                    </span>
                  </div>
                ))}
                {/* Progress line */}
                <div className="absolute top-5 left-0 right-0 h-0.5 bg-muted -z-0">
                  <div 
                    className="h-full bg-primary transition-all"
                    style={{ width: `${(activeStep / (guide.length - 1)) * 100}%` }}
                  ></div>
                </div>
              </div>
            </motion.div>

            {/* Step Content */}
            <motion.div
              key={activeStep}
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              className="mb-8"
            >
              {guide[activeStep] && (
                <Card className="bg-white border-border/40 relative overflow-hidden">
                  <CardContent className="p-8">
                    {/* OTTO helper */}
                    <div className="absolute -right-6 -bottom-6 opacity-10">
                      <img src={OTTO_URL} alt="" className="w-40" />
                    </div>
                    <div className="flex items-start gap-6 relative z-10">
                      <div className="w-16 h-16 bg-primary/10 rounded-2xl flex items-center justify-center flex-shrink-0">
                        <span className="text-2xl font-bold text-primary">{activeStep + 1}</span>
                      </div>
                      <div className="flex-1">
                        <h2 className="text-2xl font-bold text-foreground mb-4">
                          {guide[activeStep].titolo}
                        </h2>
                        <p className="text-lg text-muted-foreground mb-6">
                          {guide[activeStep].descrizione}
                        </p>
                        
                        {guide[activeStep].documenti_necessari?.length > 0 && (
                          <div className="bg-lime-50 rounded-xl p-4 border border-lime-200">
                            <h4 className="font-semibold text-primary mb-3 flex items-center gap-2">
                              <FileText className="w-4 h-4" />
                              Documenti necessari
                            </h4>
                            <ul className="space-y-2">
                              {guide[activeStep].documenti_necessari.map((doc, i) => (
                                <li key={i} className="flex items-center gap-2 text-primary">
                                  <CheckCircle className="w-4 h-4 text-green-500" />
                                  {doc}
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}
            </motion.div>

            {/* Navigation Buttons */}
            <div className="flex justify-between">
              <Button
                variant="outline"
                className="rounded-full"
                onClick={() => setActiveStep(Math.max(0, activeStep - 1))}
                disabled={activeStep === 0}
                data-testid="prev-step-btn"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Precedente
              </Button>
              
              {activeStep < guide.length - 1 ? (
                <Button
                  className="rounded-full bg-primary text-white"
                  onClick={() => setActiveStep(Math.min(guide.length - 1, activeStep + 1))}
                  data-testid="next-step-btn"
                >
                  Successivo
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              ) : (
                <Link to="/offers">
                  <Button
                    className="rounded-full bg-primary text-white"
                    data-testid="view-offers-btn"
                  >
                    Vedi le offerte
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </Link>
              )}
            </div>
          </>
        )}

        {/* Contact Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="mt-12"
        >
          <Card className="bg-gradient-to-br from-primary/5 to-cyan-500/5 border-border/40 relative overflow-hidden">
            <CardContent className="p-8">
              {/* OTTO helper */}
              <div className="absolute -left-4 top-1/2 -translate-y-1/2 opacity-20 hidden md:block">
                <img src={OTTO_URL} alt="" className="w-32" />
              </div>
              <div className="text-center mb-6 relative z-10">
                <h3 className="text-xl font-bold text-foreground mb-2">
                  Hai bisogno di aiuto?
                </h3>
                <p className="text-muted-foreground">
                  Il nostro team è a disposizione per guidarti nel passaggio
                </p>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 relative z-10">
                <a href="https://wa.me/393927451191" target="_blank" rel="noopener noreferrer" className="block">
                  <Card className="bg-white hover:shadow-md transition-shadow cursor-pointer group">
                    <CardContent className="p-4 flex items-center gap-3">
                      <div className="w-10 h-10 bg-green-100 rounded-xl flex items-center justify-center group-hover:bg-green-200 transition-colors">
                        <svg className="w-5 h-5 text-green-600" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                        </svg>
                      </div>
                      <div>
                        <p className="text-sm text-muted-foreground">WhatsApp</p>
                        <p className="font-semibold text-foreground">392 745 1191</p>
                      </div>
                    </CardContent>
                  </Card>
                </a>
                
                <Card className="bg-white">
                  <CardContent className="p-4 flex items-center gap-3">
                    <div className="w-10 h-10 bg-primary/10 rounded-xl flex items-center justify-center">
                      <Mail className="w-5 h-5 text-primary" />
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Email</p>
                      <p className="font-semibold text-foreground">info@8energysolutions.com</p>
                    </div>
                  </CardContent>
                </Card>
                
                <Card className="bg-white">
                  <CardContent className="p-4 flex items-center gap-3">
                    <div className="w-10 h-10 bg-primary/10 rounded-xl flex items-center justify-center">
                      <Clock className="w-5 h-5 text-primary" />
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Orari</p>
                      <p className="font-semibold text-foreground">Lun-Ven 9-18</p>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Social Links */}
              <div className="mt-6 text-center relative z-10">
                <p className="text-sm text-muted-foreground mb-3">Seguici sui social</p>
                <div className="flex justify-center gap-4">
                  <a href="https://www.linkedin.com/company/8energy-srl/about/" target="_blank" rel="noopener noreferrer" className="w-10 h-10 bg-blue-600 rounded-full flex items-center justify-center text-white hover:bg-blue-700 transition-colors">
                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/></svg>
                  </a>
                  <a href="https://www.facebook.com/profile.php?id=61584844715791&locale=it_IT" target="_blank" rel="noopener noreferrer" className="w-10 h-10 bg-blue-500 rounded-full flex items-center justify-center text-white hover:bg-blue-600 transition-colors">
                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
                  </a>
                  <a href="https://www.instagram.com/8energy_solutions/" target="_blank" rel="noopener noreferrer" className="w-10 h-10 bg-gradient-to-br from-purple-600 via-pink-500 to-orange-400 rounded-full flex items-center justify-center text-white hover:opacity-90 transition-opacity">
                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/></svg>
                  </a>
                  <a href="https://www.tiktok.com/@8energy_solutions" target="_blank" rel="noopener noreferrer" className="w-10 h-10 bg-black rounded-full flex items-center justify-center text-white hover:bg-gray-800 transition-colors">
                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-5.2 1.74 2.89 2.89 0 012.31-4.64 2.93 2.93 0 01.88.13V9.4a6.84 6.84 0 00-1-.05A6.33 6.33 0 005 20.1a6.34 6.34 0 0010.86-4.43v-7a8.16 8.16 0 004.77 1.52v-3.4a4.85 4.85 0 01-1-.1z"/></svg>
                  </a>
                </div>
              </div>
            </CardContent>
          </Card>
        </motion.div>
      </main>
    </div>
  );
}
